import { useEffect, useRef } from 'react'
import {
  APARENCIA_NEUTRA,
  NIVEIS_DE_RETOQUE,
  aparenciaEstaNeutra,
  nivelDoRetoque,
  rotuloDoGrau,
  valorDoNivel,
  type Aparencia,
} from './aparencia.ts'

/**
 * Painel de APARÊNCIA da câmera (SPEC-20260918-0040).
 *
 * Três ajustes contínuos, no desenho da aba "Aparência" do Meet, que o usuário
 * mostrou como referência. Cada um responde na hora, sem recriar o vídeo — dá
 * para arrastar e ver o efeito na própria prévia.
 *
 * Deixei de fora, e está escrito na tela para não virar expectativa frustrada:
 * maquiagem e iluminação direcional. Nenhuma das duas é ajuste de imagem — a
 * primeira precisa saber onde estão lábios e olhos, a segunda reacende o rosto
 * como se a luz viesse de outro lugar. São modelos, não filtros.
 */

/**
 * Só clarear e nitidez são contínuos.
 *
 * O retoque virou interruptor + dois níveis, no desenho que o usuário mostrou
 * do Meet: para suavização, "Sutil" e "Suave" dizem mais do que uma
 * porcentagem. Luz e nitidez continuam em barra porque aí o grau exato importa
 * — depende da janela e da câmera de cada um.
 */
const AJUSTES: { chave: keyof Aparencia; nome: string; icone: string; dica: string }[] = [
  {
    chave: 'clarear',
    nome: 'Clarear',
    icone: 'fas fa-sun',
    dica: 'Para sala com luz de teto ou contraluz de janela.',
  },
  {
    chave: 'nitidez',
    nome: 'Nitidez',
    icone: 'fas fa-adjust',
    dica: 'Realça contornos. Em excesso, marca a textura da pele.',
  },
]

export function MenuDeAparencia({
  botao,
  aparencia,
  onFechar,
  onTrocar,
}: {
  /** O botão que abriu — clicar nele de novo é fechar, não "clicar fora". */
  botao: HTMLElement
  aparencia: Aparencia
  onFechar: () => void
  onTrocar: (nova: Aparencia) => void
}) {
  const mudar = (chave: keyof Aparencia, valor: number) => onTrocar({ ...aparencia, [chave]: valor })
  const caixa = useRef<HTMLElement>(null)

  /*
   * Clicar fora fecha (pedido do usuário, 2026-09-18 23:44).
   *
   * Enquanto o painel era popover isto vinha de graça do scrim; como coluna,
   * não há scrim — e não deve haver, porque o ponto de estar na lateral é
   * poder mexer na imagem com a imagem à vista.
   *
   * `mousedown`, não `click`: o clique que começa numa barra e termina fora
   * dela não pode fechar o painel no meio do arrasto.
   */
  const fecharRef = useRef(onFechar)
  fecharRef.current = onFechar
  useEffect(() => {
    const aoApertar = (e: MouseEvent) => {
      const alvo = e.target as Node
      if (caixa.current?.contains(alvo) || botao.contains(alvo)) return
      fecharRef.current()
    }
    document.addEventListener('mousedown', aoApertar)
    return () => document.removeEventListener('mousedown', aoApertar)
  }, [botao])

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // a antessala inteira também fecha no Esc: sem isto, fechar o painel
      // cancelaria a chamada junto
      e.stopPropagation()
      fecharRef.current()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [])

  return (
    <aside ref={caixa} role="dialog" aria-label="Aparência da câmera" className="vc-aparencia">
      <h3 className="vc-aparelhos__titulo">
        <i className="fas fa-wand-magic-sparkles" aria-hidden /> Aparência
      </h3>

      {/* Retoque: interruptor e dois níveis com nome, como no Meet
          (referência do usuário, 2026-09-18 01:02). Ninguém sabe dizer se quer
          37% de suavização; todo mundo sabe escolher entre pouco e mais. */}
      <div className="vc-aparencia__ajuste">
        <div className="vc-aparencia__topo">
          <span className="vc-aparencia__nome">
            <i className="fas fa-spa" aria-hidden /> Retoque de aparência
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={aparencia.suavizar > 0}
            aria-label="Ligar o retoque de aparência"
            className={`vc-chave${aparencia.suavizar > 0 ? ' vc-chave--on' : ''}`}
            onClick={() => mudar('suavizar', aparencia.suavizar > 0 ? 0 : valorDoNivel('suave'))}
          >
            <span className="vc-chave__bola" aria-hidden />
          </button>
        </div>
        {aparencia.suavizar > 0 ? (
          <>
            <div className="vc-aparencia__niveis" role="radiogroup" aria-label="Grau do retoque">
              {NIVEIS_DE_RETOQUE.map((n) => {
                const escolhido = nivelDoRetoque(aparencia.suavizar) === n.id
                return (
                  <button
                    key={n.id}
                    type="button"
                    role="radio"
                    aria-checked={escolhido}
                    className={`vc-aparencia__nivel${escolhido ? ' vc-aparencia__nivel--on' : ''}`}
                    onClick={() => mudar('suavizar', n.valor)}
                  >
                    {n.nome}
                  </button>
                )
              })}
            </div>
            <span className="vc-aparencia__dica">Foco suave. Em excesso, os olhos perdem definição.</span>
          </>
        ) : null}
      </div>

      {AJUSTES.map(({ chave, nome, icone, dica }) => (
        <div className="vc-aparencia__ajuste" key={chave}>
          <label className="vc-aparencia__topo" htmlFor={`aparencia-${chave}`}>
            <span className="vc-aparencia__nome">
              <i className={icone} aria-hidden /> {nome}
            </span>
            <span className="vc-aparencia__valor">{rotuloDoGrau(aparencia[chave])}</span>
          </label>
          <input
            id={`aparencia-${chave}`}
            className="vc-aparencia__barra"
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(aparencia[chave] * 100)}
            aria-describedby={`aparencia-dica-${chave}`}
            onChange={(e) => mudar(chave, Number(e.target.value) / 100)}
          />
          <span className="vc-aparencia__dica" id={`aparencia-dica-${chave}`}>
            {dica}
          </span>
        </div>
      ))}

      <button
        type="button"
        className="vc-aparelhos__botao"
        disabled={aparenciaEstaNeutra(aparencia)}
        onClick={() => onTrocar(APARENCIA_NEUTRA)}
      >
        <i className="fas fa-rotate-left" aria-hidden /> Voltar ao natural
      </button>

      <p className="vc-fundo__nota">
        Maquiagem e iluminação de estúdio não estão aqui: as duas exigem um modelo que reconheça o
        rosto, não um ajuste de imagem.
      </p>
    </aside>
  )
}
